import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { RefreshCw, UserCheck, CheckCircle2, XCircle, Clock, AlertTriangle, Briefcase, LifeBuoy, Filter, Settings2, Mail, Trash2 } from "lucide-react";
import { api, formatApiErrorDetail } from "../api";
import { PageHeader, Btn, Badge } from "../components/common";
import { useAuth } from "../auth";
import { overlayCloseHandlers } from "../lib/overlayClose";
import { useAutoRefresh } from "../lib/autoRefresh";

const ESTADO_TABS = [
  { value: "abertos", label: "Abertos + Em curso", filter: { estado: "" } },
  { value: "aberto", label: "Novos", filter: { estado: "aberto" } },
  { value: "em_curso", label: "Em curso", filter: { estado: "em_curso" } },
  { value: "resolvido", label: "Resolvidos", filter: { estado: "resolvido" } },
  { value: "todos", label: "Todos", filter: { estado: "todos" } },
];

const TIPO_LABEL = { comercial: "Comercial", tecnico: "Suporte tecnico" };
const TIPO_COLOR = { comercial: "bg-blue-50 text-blue-700 border-blue-200",
                     tecnico: "bg-amber-50 text-amber-700 border-amber-200" };
const ORIGEM_LABEL = { "chat-ai": "Chat", "formulario": "Formulario", "email": "Email" };
const ORIGEM_COLOR = { "chat-ai": "bg-indigo-50 text-indigo-700 border-indigo-200",
                       "formulario": "bg-emerald-50 text-emerald-700 border-emerald-200",
                       "email": "bg-slate-100 text-slate-600 border-slate-200" };
const BADGE_STYLE = {
  ok: "bg-emerald-50 text-emerald-700 border-emerald-200",
  urgente: "bg-orange-50 text-orange-700 border-orange-200",
  atrasado: "bg-red-50 text-red-700 border-red-200",
  respondido: "bg-slate-100 text-slate-600 border-slate-200",
};

function fmtMin(m) {
  if (m == null) return "-";
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h}h ${r}m` : `${h}h`;
}

function WorkflowModal({ title, onClose, children, testid, wide = false }) {
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/40" {...overlayCloseHandlers(onClose)} data-testid={testid}>
      <div className={`bg-white rounded-lg border border-tint ${wide ? "w-[min(760px,94vw)]" : "w-[min(520px,92vw)]"} max-h-[85vh] overflow-y-auto shadow-xl`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 border-b border-tint bg-slate-50">
          <div className="text-sm font-bold text-navy">{title}</div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700 text-lg" data-testid={`${testid}-close`}>×</button>
        </div>
        <div className="p-4">{children}</div>
      </div>
    </div>
  );
}

function SlaChip({ sla }) {
  if (!sla) return null;
  const badge = sla.badge || "ok";
  const label = badge === "respondido"
    ? `Respondido em ${fmtMin(sla.elapsed_min)}`
    : badge === "atrasado"
      ? `Atraso: ${fmtMin(sla.elapsed_min - sla.sla_min)}`
      : badge === "urgente"
        ? `Falta ${fmtMin(sla.due_in_min)}`
        : `${fmtMin(sla.due_in_min)} restantes`;
  const Icon = badge === "atrasado" ? AlertTriangle : (badge === "respondido" ? CheckCircle2 : Clock);
  return (
    <span className={`inline-flex items-center gap-1 text-[10px] font-semibold px-2 py-0.5 rounded-full border ${BADGE_STYLE[badge]}`} data-testid={`handoff-sla-${badge}`}>
      <Icon size={10} /> {label}
    </span>
  );
}

export default function Handoffs() {
  const { can } = useAuth();
  const isSuperAdmin = can("*");
  const [tab, setTab] = useState("abertos");
  const [tipoFilter, setTipoFilter] = useState("");
  const [origemFilter, setOrigemFilter] = useState("");
  const [rows, setRows] = useState([]);
  const [counts, setCounts] = useState({ total_abertos: 0, comercial: 0, tecnico: 0, atrasados: 0, urgentes: 0 });
  const [sel, setSel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [nota, setNota] = useState("");
  const [assignee, setAssignee] = useState("");
  const [showSla, setShowSla] = useState(false);
  const [slaCfg, setSlaCfg] = useState({ default_comercial_h: 2, default_tecnico_h: 1, per_platform: {}, auto_assign: true, supervisor_email: "", supervisor_telegram_chat_id: "" });
  const [slaSaving, setSlaSaving] = useState(false);
  const [newPlatform, setNewPlatform] = useState("");
  const [tgStatus, setTgStatus] = useState({ configured: false, enabled: false, bot_username: null });
  const [tgTesting, setTgTesting] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [analyticsDias, setAnalyticsDias] = useState(7);
  const [showAnalytics, setShowAnalytics] = useState(true);
  // Modais de workflow (Iter98d)
  const [showAssign, setShowAssign] = useState(false);
  const [showResolve, setShowResolve] = useState(false);
  const [showCancel, setShowCancel] = useState(false);
  const [showCreateTask, setShowCreateTask] = useState(false);
  const [showEmail, setShowEmail] = useState(false);
  const [teamMembers, setTeamMembers] = useState([]);
  const [emailTemplates, setEmailTemplates] = useState([]);
  const [resolveNota, setResolveNota] = useState("");
  const [cancelMotivo, setCancelMotivo] = useState("");
  const [assignForm, setAssignForm] = useState({ email: "", nota: "" });
  const [taskForm, setTaskForm] = useState({ titulo: "", descricao: "", tipo: "follow-up", prazo: "", prioridade: "media" });
  const [emailForm, setEmailForm] = useState({ template_id: "", para: "", assunto: "", corpo_html: "" });
  const [modalBusy, setModalBusy] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [expandedEmails, setExpandedEmails] = useState(new Set());
  const [agentSignature, setAgentSignature] = useState("");
  const [inclSignature, setInclSignature] = useState(false);

  const currentFilter = useMemo(() => ESTADO_TABS.find((t) => t.value === tab)?.filter || {}, [tab]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { ...currentFilter };
      if (tipoFilter) params.tipo = tipoFilter;
      if (origemFilter) params.origem = origemFilter;
      const { data } = await api.get("/chat-ai/handoffs", { params });
      setRows(data?.handoffs || []);
      setCounts(data?.counts || { total_abertos: 0, comercial: 0, tecnico: 0, atrasados: 0, urgentes: 0 });
      if (sel) {
        const upd = (data?.handoffs || []).find((h) => h.id === sel.id);
        setSel(upd || null);
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const loadAnalytics = async (dias = analyticsDias) => {
    try {
      const { data } = await api.get("/chat-ai/handoffs/analytics", { params: { dias } });
      setAnalytics(data);
    } catch (e) { /* silent */ }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [tab, tipoFilter, origemFilter]);
  useAutoRefresh(load);
  useEffect(() => { loadAnalytics(analyticsDias); /* eslint-disable-next-line */ }, [analyticsDias]);
  useEffect(() => {
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    /* eslint-disable-next-line */
  }, [tab, tipoFilter, origemFilter]);

  // Carrega team members e templates ao montar (para modais)
  useEffect(() => {
    api.get("/team/members").then(({ data }) => setTeamMembers(data || [])).catch(() => setTeamMembers([]));
    api.get("/email-templates").then(({ data }) => setEmailTemplates(data || [])).catch(() => setEmailTemplates([]));
    api.get("/me").then(({ data }) => setAgentSignature(data?.assinatura_email || "")).catch(() => {});
  }, []);

  // Carrega historico de emails quando o handoff selecionado muda
  const loadEmailHistory = async (hid) => {
    if (!hid) { setEmailHistory([]); return; }
    try {
      const { data } = await api.get(`/chat-ai/handoffs/${hid}/emails`);
      setEmailHistory(data?.emails || []);
    } catch (_) { setEmailHistory([]); }
  };
  useEffect(() => { loadEmailHistory(sel?.id); setExpandedEmails(new Set()); /* eslint-disable-next-line */ }, [sel?.id]);

  // Handlers dos modais
  const submitAssign = async () => {
    if (!sel) return;
    if (!assignForm.email.trim()) { toast.error("Escolha o responsavel."); return; }
    setModalBusy(true);
    try {
      await api.patch(`/chat-ai/handoffs/${sel.id}`, {
        atribuido_a: assignForm.email.trim(),
        nota: assignForm.nota || null,
        estado: "em_curso",
      });
      toast.success("Handoff atribuido.");
      setShowAssign(false); setAssignForm({ email: "", nota: "" });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setModalBusy(false); }
  };

  const submitResolve = async () => {
    if (!sel) return;
    if (!resolveNota.trim() && !sel.primeira_resposta_em && !(sel.notas || []).length) {
      toast.error("Registe uma nota de resolucao com o que foi feito e resultado.");
      return;
    }
    setModalBusy(true);
    try {
      await api.patch(`/chat-ai/handoffs/${sel.id}`, { estado: "resolvido", nota: resolveNota || null });
      toast.success("Handoff resolvido.");
      setShowResolve(false); setResolveNota("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setModalBusy(false); }
  };

  const submitCancel = async () => {
    if (!sel) return;
    if (!cancelMotivo.trim()) { toast.error("Indique o motivo do cancelamento."); return; }
    setModalBusy(true);
    try {
      await api.patch(`/chat-ai/handoffs/${sel.id}`, { estado: "cancelado", nota: `[cancelado] ${cancelMotivo}` });
      toast.success("Handoff cancelado.");
      setShowCancel(false); setCancelMotivo("");
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setModalBusy(false); }
  };

  const submitDelete = async () => {
    if (!sel) return;
    setDeleting(true);
    try {
      await api.delete(`/chat-ai/handoffs/${sel.id}`);
      toast.success("Handoff eliminado.");
      setShowDeleteConfirm(false);
      setSel(null);
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setDeleting(false); }
  };

  const submitCreateTask = async () => {
    if (!sel) return;
    if (!taskForm.titulo.trim()) { toast.error("Indique o titulo da tarefa."); return; }
    setModalBusy(true);
    try {
      await api.post(`/chat-ai/handoffs/${sel.id}/create-task`, taskForm);
      toast.success("Tarefa criada e ligada ao handoff.");
      setShowCreateTask(false); setTaskForm({ titulo: "", descricao: "", tipo: "follow-up", prazo: "", prioridade: "media" });
      await load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setModalBusy(false); }
  };

  const submitEmail = async () => {
    if (!sel) return;
    if (!emailForm.template_id && !emailForm.corpo_html.trim()) {
      toast.error("Escolha um template OU escreva um corpo manual.");
      return;
    }
    setModalBusy(true);
    try {
      const corpoFinal = inclSignature && agentSignature
        ? `${emailForm.corpo_html}\n<hr style="margin:16px 0;border:none;border-top:1px solid #e2e8f0;" />${agentSignature}`
        : emailForm.corpo_html;
      const { data } = await api.post(`/chat-ai/handoffs/${sel.id}/send-email`, { ...emailForm, corpo_html: corpoFinal });
      toast.success(`Email enviado a ${data.para} (${data.estado}).`);
      setShowEmail(false); setEmailForm({ template_id: "", para: "", assunto: "", corpo_html: "" }); setInclSignature(false);
      await load();
      loadEmailHistory(sel?.id);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setModalBusy(false); }
  };

  // Filtra templates pela plataforma do handoff (se conhecida)
  const filteredTemplates = (() => {
    if (!sel) return emailTemplates;
    const plat = (sel.plataforma || "").toLowerCase();
    if (!plat) return emailTemplates.filter((t) => !t.plataforma);
    return emailTemplates.filter((t) => (t.plataforma || "").toLowerCase() === plat || !t.plataforma);
  })();

  const update = async (payload) => {
    if (!sel) return;
    // Exigencia de evidencia: para resolver/cancelar precisa de nota, salvo se ja
    // existir uma resposta do agente na sessao (validado tambem no backend).
    if (payload && (payload.estado === "resolvido" || payload.estado === "cancelado")) {
      const jaTemResposta = !!sel.primeira_resposta_em;
      const jaTemNotas = Array.isArray(sel.notas) && sel.notas.length > 0;
      const notaTxt = (nota || "").trim();
      if (!notaTxt && !jaTemResposta && !jaTemNotas) {
        toast.error("Registe uma nota de resolucao (ou responda ao cliente) antes de marcar como " + payload.estado + ".");
        return;
      }
    }
    try {
      await api.patch(`/chat-ai/handoffs/${sel.id}`, payload);
      toast.success("Handoff actualizado.");
      setNota("");
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const loadSla = async () => {
    try {
      const { data } = await api.get("/chat-ai/handoffs/sla-config");
      setSlaCfg({
        default_comercial_h: Number(data?.default_comercial_h ?? 2),
        default_tecnico_h: Number(data?.default_tecnico_h ?? 1),
        per_platform: data?.per_platform || {},
        auto_assign: !!data?.auto_assign,
        supervisor_email: data?.supervisor_email || "",
        supervisor_telegram_chat_id: data?.supervisor_telegram_chat_id || "",
      });
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };
  const saveSla = async () => {
    setSlaSaving(true);
    try {
      await api.put("/chat-ai/handoffs/sla-config", slaCfg);
      toast.success("SLA guardado.");
      setShowSla(false);
      await load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setSlaSaving(false);
    }
  };
  const openSlaModal = async () => {
    await loadSla();
    try {
      const { data } = await api.get("/security/telegram/status");
      setTgStatus(data || { configured: false, enabled: false, bot_username: null });
    } catch { /* silent */ }
    setShowSla(true);
  };
  const testTelegram = async () => {
    setTgTesting(true);
    try {
      const { data } = await api.post("/chat-ai/handoffs/notify-test");
      if (data?.ok) toast.success("Mensagem de teste enviada por Telegram.");
      else toast.error("Telegram nao configurado ou envio falhou. Configure em Definicoes > Canais > Telegram.");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Falha ao testar Telegram.");
    } finally {
      setTgTesting(false);
    }
  };
  const setPerPlatform = (name, key, value) => {
    setSlaCfg((c) => ({
      ...c,
      per_platform: {
        ...c.per_platform,
        [name]: { ...(c.per_platform?.[name] || {}), [key]: value === "" ? null : Number(value) },
      },
    }));
  };
  const removePerPlatform = (name) => {
    setSlaCfg((c) => {
      const p = { ...(c.per_platform || {}) };
      delete p[name];
      return { ...c, per_platform: p };
    });
  };

  return (
    <>
      <PageHeader
        overline="Suporte / Comercial"
        title="Fila de Handoffs (pedidos humanos)"
        action={
          <div className="flex gap-2">
            <Btn variant="ghost" onClick={openSlaModal} testid="handoffs-sla-btn"><Settings2 size={14} /> SLA / Auto-atribuir</Btn>
            <Btn variant="ghost" onClick={load} testid="handoffs-refresh"><RefreshCw size={14} /> Atualizar</Btn>
          </div>
        }
      />

      {/* Painel analytics SLA (colapsavel) */}
      <div className="border border-tint rounded-lg bg-white mb-3" data-testid="handoffs-analytics">
        <button onClick={() => setShowAnalytics((v) => !v)} className="w-full flex items-center justify-between px-4 py-2 text-left" data-testid="handoffs-analytics-toggle">
          <div className="text-sm font-semibold text-navy">Metricas SLA ({analyticsDias}d)</div>
          <div className="flex items-center gap-2 text-[11px] text-slate-500">
            <span className={`px-2 py-1 rounded border ${analyticsDias === 7 ? "bg-slate-100 border-slate-300" : "border-tint"}`}
              onClick={(e) => { e.stopPropagation(); setAnalyticsDias(7); }} data-testid="analytics-7d">7d</span>
            <span className={`px-2 py-1 rounded border ${analyticsDias === 30 ? "bg-slate-100 border-slate-300" : "border-tint"}`}
              onClick={(e) => { e.stopPropagation(); setAnalyticsDias(30); }} data-testid="analytics-30d">30d</span>
            <span className={`px-2 py-1 rounded border ${analyticsDias === 90 ? "bg-slate-100 border-slate-300" : "border-tint"}`}
              onClick={(e) => { e.stopPropagation(); setAnalyticsDias(90); }} data-testid="analytics-90d">90d</span>
            <span className="text-slate-400">{showAnalytics ? "▲" : "▼"}</span>
          </div>
        </button>
        {showAnalytics && analytics && (
          <div className="px-4 pb-3 border-t border-tint pt-3 space-y-3">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
              <div className="p-2 border border-tint rounded"><div className="text-[10px] uppercase text-slate-500">Total pedidos</div><div className="text-lg font-bold text-navy">{analytics.total || 0}</div></div>
              <div className="p-2 border border-tint rounded"><div className="text-[10px] uppercase text-slate-500">Tempo medio 1a resposta</div><div className="text-lg font-bold text-navy">{analytics.tempo_medio_resposta_min != null ? `${analytics.tempo_medio_resposta_min} min` : "-"}</div></div>
              <div className="p-2 border border-tint rounded"><div className="text-[10px] uppercase text-slate-500">Cumprimento SLA</div><div className={`text-lg font-bold ${(analytics.sla_cumprimento_pct ?? 100) >= 90 ? "text-emerald-700" : (analytics.sla_cumprimento_pct ?? 100) >= 70 ? "text-amber-700" : "text-red-600"}`}>{analytics.sla_cumprimento_pct != null ? `${analytics.sla_cumprimento_pct}%` : "-"}</div></div>
              <div className="p-2 border border-tint rounded"><div className="text-[10px] uppercase text-slate-500">SLA quebrado</div><div className={`text-lg font-bold ${analytics.breaches ? "text-red-600" : "text-emerald-700"}`}>{analytics.breaches || 0}</div></div>
            </div>

            {/* Grafico diario (bar chart simples) */}
            <div>
              <div className="text-[10px] uppercase text-slate-500 mb-1">Pedidos por dia (comercial vs tecnico)</div>
              <div className="flex items-end gap-1 h-24 border-b border-l border-tint p-1" data-testid="analytics-chart-daily">
                {(analytics.serie_diaria || []).length === 0 && <div className="text-xs text-slate-400 italic self-center">Sem dados no periodo.</div>}
                {(() => {
                  const rows2 = analytics.serie_diaria || [];
                  const maxV = Math.max(1, ...rows2.map((r) => r.total || 0));
                  return rows2.map((r) => (
                    <div key={r.dia} className="flex-1 flex flex-col items-center gap-0.5" title={`${r.dia}: ${r.total} (com ${r.comercial}, tec ${r.tecnico})`}>
                      <div className="w-full flex flex-col justify-end" style={{ height: "88px" }}>
                        <div className="w-full bg-blue-500" style={{ height: `${((r.comercial || 0) / maxV) * 88}px` }} />
                        <div className="w-full bg-amber-500" style={{ height: `${((r.tecnico || 0) / maxV) * 88}px` }} />
                      </div>
                      <div className="text-[9px] text-slate-500 truncate w-full text-center">{r.dia.slice(5)}</div>
                    </div>
                  ));
                })()}
              </div>
              <div className="flex items-center gap-3 mt-1 text-[10px] text-slate-500">
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 bg-blue-500 inline-block rounded-sm" /> Comercial</span>
                <span className="inline-flex items-center gap-1"><span className="w-2.5 h-2.5 bg-amber-500 inline-block rounded-sm" /> Tecnico</span>
              </div>
            </div>

            {/* Tabela por plataforma */}
            <div>
              <div className="text-[10px] uppercase text-slate-500 mb-1">Por plataforma</div>
              <div className="overflow-x-auto">
                <table className="w-full text-[11px]">
                  <thead>
                    <tr className="text-left text-slate-500 border-b border-tint">
                      <th className="py-1 pr-2">Plataforma</th><th className="pr-2">Total</th><th className="pr-2">Com.</th><th className="pr-2">Tec.</th><th className="pr-2">Media (min)</th><th>SLA %</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(analytics.plataformas || []).length === 0 && (
                      <tr><td colSpan={6} className="py-2 text-center text-slate-400 italic">Sem dados.</td></tr>
                    )}
                    {(analytics.plataformas || []).map((p) => (
                      <tr key={p.plataforma} className="border-b border-slate-100 last:border-0" data-testid={`analytics-plat-${p.plataforma}`}>
                        <td className="py-1 pr-2 font-semibold text-navy">{p.plataforma}</td>
                        <td className="pr-2">{p.total}</td>
                        <td className="pr-2 text-blue-700">{p.comercial}</td>
                        <td className="pr-2 text-amber-700">{p.tecnico}</td>
                        <td className="pr-2">{p.primeira_resposta_media_min ?? "-"}</td>
                        <td className={p.sla_cumprimento_pct == null ? "text-slate-400" : p.sla_cumprimento_pct >= 90 ? "text-emerald-700 font-semibold" : p.sla_cumprimento_pct >= 70 ? "text-amber-700" : "text-red-600 font-semibold"}>{p.sla_cumprimento_pct != null ? `${p.sla_cumprimento_pct}%` : "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3">
        <div className="border border-tint rounded-md p-2.5 bg-white" data-testid="handoffs-kpi-total">
          <div className="text-[10px] uppercase text-slate-500">Total abertos</div>
          <div className="text-xl font-bold text-navy">{counts.total_abertos}</div>
        </div>
        <div className="border border-tint rounded-md p-2.5 bg-white" data-testid="handoffs-kpi-comercial">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Briefcase size={10} /> Comercial</div>
          <div className="text-xl font-bold text-blue-700">{counts.comercial}</div>
        </div>
        <div className="border border-tint rounded-md p-2.5 bg-white" data-testid="handoffs-kpi-tecnico">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><LifeBuoy size={10} /> Tecnico</div>
          <div className="text-xl font-bold text-amber-700">{counts.tecnico}</div>
        </div>
        <div className="border border-tint rounded-md p-2.5 bg-white" data-testid="handoffs-kpi-urgentes">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><Clock size={10} /> Urgentes (&lt;15m)</div>
          <div className="text-xl font-bold text-orange-600">{counts.urgentes}</div>
        </div>
        <div className="border border-tint rounded-md p-2.5 bg-white" data-testid="handoffs-kpi-atrasados">
          <div className="text-[10px] uppercase text-slate-500 flex items-center gap-1"><AlertTriangle size={10} /> Atrasados</div>
          <div className="text-xl font-bold text-red-600">{counts.atrasados}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="flex flex-wrap gap-2 mb-3" data-testid="handoffs-tabs">
        {ESTADO_TABS.map((t) => (
          <button key={t.value} onClick={() => setTab(t.value)} data-testid={`handoffs-tab-${t.value}`}
            className={`text-xs px-3 py-1.5 rounded ${tab === t.value ? "bg-navy text-white" : "border border-tint text-slate-600 hover:bg-slate-50"}`}>
            {t.label}
          </button>
        ))}
        <div className="ml-auto flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
          <Filter size={12} />
          <button onClick={() => setTipoFilter("")} className={`px-2 py-1 rounded border ${!tipoFilter ? "bg-slate-100 border-slate-300" : "border-tint"}`} data-testid="handoffs-tipo-all">Todos tipos</button>
          <button onClick={() => setTipoFilter("comercial")} className={`px-2 py-1 rounded border ${tipoFilter === "comercial" ? "bg-blue-50 border-blue-300 text-blue-700" : "border-tint"}`} data-testid="handoffs-tipo-comercial">Comercial</button>
          <button onClick={() => setTipoFilter("tecnico")} className={`px-2 py-1 rounded border ${tipoFilter === "tecnico" ? "bg-amber-50 border-amber-300 text-amber-700" : "border-tint"}`} data-testid="handoffs-tipo-tecnico">Tecnico</button>
          <span className="text-slate-300">|</span>
          <button onClick={() => setOrigemFilter("")} className={`px-2 py-1 rounded border ${!origemFilter ? "bg-slate-100 border-slate-300" : "border-tint"}`} data-testid="handoffs-origem-all">Todas origens</button>
          <button onClick={() => setOrigemFilter("chat-ai")} className={`px-2 py-1 rounded border ${origemFilter === "chat-ai" ? "bg-indigo-50 border-indigo-300 text-indigo-700" : "border-tint"}`} data-testid="handoffs-origem-chat">Chat</button>
          <button onClick={() => setOrigemFilter("formulario")} className={`px-2 py-1 rounded border ${origemFilter === "formulario" ? "bg-emerald-50 border-emerald-300 text-emerald-700" : "border-tint"}`} data-testid="handoffs-origem-form">Formulario</button>
          <button onClick={() => setOrigemFilter("email")} className={`px-2 py-1 rounded border ${origemFilter === "email" ? "bg-slate-100 border-slate-300" : "border-tint"}`} data-testid="handoffs-origem-email">Email</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {/* Lista */}
        <div className="md:col-span-1 space-y-1.5 max-h-[75vh] overflow-y-auto" data-testid="handoffs-list">
          {loading && rows.length === 0 && <div className="text-center text-muted2 text-xs py-6">A carregar...</div>}
          {!loading && rows.length === 0 && <div className="text-center text-muted2 text-xs py-6">Sem pedidos.</div>}
          {rows.map((h) => (
            <button key={h.id} onClick={() => { setSel(h); setAssignee(h.atribuido_a || ""); }}
              data-testid={`handoff-row-${h.id}`}
              className={`w-full text-left border rounded-md p-2.5 transition ${sel?.id === h.id ? "border-blue bg-blue/5" : "border-tint bg-white hover:border-blue"}`}>
              <div className="flex items-center justify-between gap-2 mb-1">
                <div className="flex items-center gap-1">
                  <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${TIPO_COLOR[h.tipo] || "bg-slate-50"}`}>{TIPO_LABEL[h.tipo] || h.tipo}</span>
                  {h.origem && <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded border ${ORIGEM_COLOR[h.origem] || "bg-slate-50"}`}>{ORIGEM_LABEL[h.origem] || h.origem}</span>}
                </div>
                <SlaChip sla={h.sla} />
              </div>
              <div className="text-sm font-semibold text-navy truncate">{h.nome || "Visitante"}</div>
              <div className="text-[10px] text-slate-500 truncate">{h.email || "-"} - {h.organizacao || "-"}</div>
              <div className="text-[11px] text-slate-500 mt-1 line-clamp-2">{h.mensagem}</div>
              <div className="flex items-center justify-between mt-1 text-[10px] text-slate-400">
                <span>{h.plataforma || "-"}</span>
                <Badge value={h.estado} />
              </div>
            </button>
          ))}
        </div>

        {/* Detalhe */}
        <div className="md:col-span-2 border border-tint rounded-lg bg-white flex flex-col min-h-[75vh]" data-testid="handoff-detail">
          {!sel && <div className="flex-1 flex items-center justify-center text-muted2 text-sm">Selecione um pedido a esquerda.</div>}
          {sel && (
            <>
              <div className="flex items-start justify-between px-4 py-3 border-b border-tint bg-slate-50 gap-3">
                <div className="min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`text-[10px] font-bold px-2 py-0.5 rounded border ${TIPO_COLOR[sel.tipo]}`}>{TIPO_LABEL[sel.tipo]}</span>
                    <SlaChip sla={sel.sla} />
                    <Badge value={sel.estado} />
                  </div>
                  <div className="text-sm font-semibold text-navy">{sel.nome || "Visitante"} <span className="text-slate-400 text-[11px] font-normal">- {sel.email}</span></div>
                  <div className="text-[11px] text-slate-500">{sel.organizacao || "-"} - {sel.telefone || "-"} - Plataforma: <b>{sel.plataforma || "-"}</b></div>
                </div>
                <div className="flex flex-col gap-1 text-right shrink-0">
                  {sel.lead_account_id && (
                    <Link to={`/app/crm/contas/${sel.lead_account_id}`} className="text-[11px] underline text-blue hover:text-blue/80" data-testid="handoff-lead-link">Ver conta CRM</Link>
                  )}
                  {sel.ticket_id && (
                    <Link to={`/app/tickets`} className="text-[11px] underline text-amber-700 hover:text-amber-800" data-testid="handoff-ticket-link">Ver ticket ({String(sel.ticket_id).slice(0, 8)})</Link>
                  )}
                  {sel.session_id && (
                    <Link to={`/app/inbox-canais?session=${encodeURIComponent(sel.session_id)}&from=handoff`} className="text-[11px] underline text-slate-600 hover:text-slate-800" data-testid="handoff-session-link">Ver conversa</Link>
                  )}
                </div>
              </div>

              <div className="p-4 space-y-3 flex-1 overflow-y-auto">
                <div>
                  <div className="text-[10px] uppercase text-slate-500 mb-1">Mensagem do cliente</div>
                  <div className="text-sm whitespace-pre-wrap p-3 bg-slate-50 border border-tint rounded" data-testid="handoff-message">{sel.mensagem}</div>
                </div>

                <div className="grid grid-cols-2 gap-3 text-[11px]">
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Criado</div>
                    <div className="text-navy">{sel.criado_em?.slice(0, 19)?.replace("T", " ") || "-"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Primeira resposta</div>
                    <div className="text-navy">{sel.primeira_resposta_em ? sel.primeira_resposta_em.slice(0, 19).replace("T", " ") : "-"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Atribuido a</div>
                    <div className="text-navy">{sel.atribuido_a || "-"}</div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase text-slate-500">Resolvido em</div>
                    <div className="text-navy">{sel.resolvido_em ? sel.resolvido_em.slice(0, 19).replace("T", " ") : "-"}</div>
                  </div>
                </div>

                {Array.isArray(sel.notas) && sel.notas.length > 0 && (
                  <div>
                    <div className="text-[10px] uppercase text-slate-500 mb-1">Notas internas</div>
                    <div className="space-y-1">
                      {sel.notas.map((n, i) => (
                        <div key={i} className="text-[11px] p-2 bg-yellow-50 border border-yellow-200 rounded">
                          <div className="text-slate-600 font-semibold">{n.por} - <span className="text-slate-400 font-normal">{n.em?.slice(0, 16)?.replace("T", " ")}</span></div>
                          <div className="text-slate-700 whitespace-pre-wrap">{n.texto}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="border-t border-tint pt-3 space-y-2">
                  {/* Historico de emails enviados */}
                  {emailHistory.length > 0 && (
                    <div className="bg-blue-50/50 border border-blue-200 rounded p-2" data-testid="handoff-email-history">
                      <div className="text-[10px] uppercase font-semibold text-blue-800 mb-1.5 flex items-center gap-1">
                        <Mail size={11} /> Emails enviados ({emailHistory.length})
                      </div>
                      <div className="space-y-1.5">
                        {emailHistory.map((e, i) => {
                          const isOpen = expandedEmails.has(i);
                          const corpoHtml = (e.corpo || "").split("\n\n").slice(1).join("\n\n").trim();
                          const toggleBody = () => setExpandedEmails((prev) => {
                            const next = new Set(prev);
                            isOpen ? next.delete(i) : next.add(i);
                            return next;
                          });
                          return (
                            <div key={i} className="text-[11px] bg-white border border-blue-100 rounded p-2" data-testid={`handoff-email-history-item-${i}`}>
                              <div className="flex items-start justify-between gap-2">
                                <div className="font-semibold text-navy truncate">{(e.assunto || "").replace("Email enviado: ", "")}</div>
                                <div className="text-slate-400 shrink-0">{(e.em || "").slice(0, 16).replace("T", " ")}</div>
                              </div>
                              <div className="flex items-center justify-between mt-0.5">
                                <div className="text-slate-500">
                                  Para: <span className="text-slate-700">{e.para || "-"}</span>
                                  {e.autor_email && <span className="ml-2 text-slate-400">por {e.autor_email}</span>}
                                </div>
                                {corpoHtml && (
                                  <button onClick={toggleBody} data-testid={`handoff-email-toggle-${i}`}
                                    className="text-[10px] text-blue-600 hover:text-blue-800 hover:underline shrink-0 ml-2 transition-colors">
                                    {isOpen ? "Ocultar" : "Ver corpo"}
                                  </button>
                                )}
                              </div>
                              {isOpen && corpoHtml && (
                                <div className="mt-2 border border-blue-100 rounded bg-slate-50 p-2 max-h-48 overflow-y-auto text-[11px] leading-relaxed"
                                  data-testid={`handoff-email-body-${i}`}
                                  dangerouslySetInnerHTML={{ __html: corpoHtml }} />
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  {Array.isArray(sel.tarefas) && sel.tarefas.length > 0 && (
                    <div className="bg-emerald-50/60 border border-emerald-200 rounded p-2" data-testid="handoff-tarefas">
                      <div className="text-[10px] uppercase font-semibold text-emerald-800 mb-1">Tarefas criadas ({sel.tarefas.length})</div>
                      <ul className="text-[11px] text-emerald-900 space-y-0.5">
                        {sel.tarefas.map((t, i) => (
                          <li key={i}>- {t.titulo}{t.prazo ? ` (prazo ${t.prazo.slice(0, 16).replace("T", " ")})` : ""}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-2">
                    <Btn variant="ghost" onClick={() => setShowAssign(true)} testid="handoff-open-assign" className="justify-center">
                      <UserCheck size={13} /> Atribuir
                    </Btn>
                    <Btn variant="ghost" onClick={() => setShowCreateTask(true)} testid="handoff-open-task" className="justify-center">
                      <Briefcase size={13} /> Criar tarefa
                    </Btn>
                    <Btn variant="ghost" onClick={() => { setEmailForm({ template_id: "", para: sel.email || "", assunto: "", corpo_html: "" }); setShowEmail(true); }} testid="handoff-open-email" className="justify-center">
                      <Mail size={13} /> Responder por email
                    </Btn>
                    <Btn variant="ghost" onClick={() => setShowResolve(true)} testid="handoff-open-resolve" className="justify-center text-emerald-700 hover:bg-emerald-50">
                      <CheckCircle2 size={13} /> Resolver
                    </Btn>
                    <Btn variant="ghost" onClick={() => setShowCancel(true)} testid="handoff-open-cancel" className="col-span-2 justify-center text-red-700 hover:bg-red-50">
                      <XCircle size={13} /> Cancelar handoff
                    </Btn>
                    {isSuperAdmin && (
                      <Btn variant="ghost" onClick={() => setShowDeleteConfirm(true)} testid="handoff-open-delete" className="col-span-2 justify-center text-red-900 hover:bg-red-100 border-red-200">
                        <Trash2 size={13} /> Eliminar handoff
                      </Btn>
                    )}
                  </div>
                  <p className="text-[10px] text-slate-500 mt-1">Todas as accoes ficam registadas no timeline da conta CRM (evidencia auditavel).</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modais de workflow do handoff (Iter98d) */}
      {sel && showAssign && (
        <WorkflowModal title="Atribuir handoff" onClose={() => setShowAssign(false)} testid="handoff-modal-assign">
          <label className="text-[11px] text-slate-600">Responsavel</label>
          <select value={assignForm.email} onChange={(e) => setAssignForm({ ...assignForm, email: e.target.value })}
            data-testid="handoff-assign-select"
            className="w-full text-sm border border-tint rounded px-2 py-1.5 mt-1">
            <option value="">-- escolher membro da equipa --</option>
            {teamMembers.map((m) => (
              <option key={m.id || m.email} value={m.email}>{m.nome ? `${m.nome} (${m.email})` : m.email}</option>
            ))}
          </select>
          <label className="text-[11px] text-slate-600 mt-2 block">Nota (opcional)</label>
          <textarea rows={2} value={assignForm.nota} onChange={(e) => setAssignForm({ ...assignForm, nota: e.target.value })}
            data-testid="handoff-assign-nota"
            placeholder="Contexto para o responsavel..."
            className="w-full text-sm border border-tint rounded p-2 mt-1" />
          <div className="flex justify-end gap-2 pt-3 mt-2 border-t border-tint">
            <Btn variant="ghost" onClick={() => setShowAssign(false)}>Cancelar</Btn>
            <Btn onClick={submitAssign} disabled={modalBusy} testid="handoff-assign-submit"><UserCheck size={13} /> Atribuir</Btn>
          </div>
        </WorkflowModal>
      )}

      {sel && showResolve && (
        <WorkflowModal title="Resolver handoff" onClose={() => setShowResolve(false)} testid="handoff-modal-resolve">
          <label className="text-[11px] font-semibold text-slate-700">Nota de resolucao <span className="text-red-600">*</span></label>
          <textarea rows={4} value={resolveNota} onChange={(e) => setResolveNota(e.target.value)}
            data-testid="handoff-resolve-nota"
            placeholder="Descreva o que foi feito, resultado, proximos passos..."
            className="w-full text-sm border border-tint rounded p-2 mt-1" />
          <p className="text-[10px] text-slate-500 mt-1">A nota fica em auditoria: timeline da conta CRM + "Ultima interacao" da ficha.</p>
          <div className="flex justify-end gap-2 pt-3 mt-2 border-t border-tint">
            <Btn variant="ghost" onClick={() => setShowResolve(false)}>Cancelar</Btn>
            <Btn onClick={submitResolve} disabled={modalBusy} testid="handoff-resolve-submit"><CheckCircle2 size={13} /> Marcar resolvido</Btn>
          </div>
        </WorkflowModal>
      )}

      {sel && showCancel && (
        <WorkflowModal title="Cancelar handoff" onClose={() => setShowCancel(false)} testid="handoff-modal-cancel">
          <label className="text-[11px] font-semibold text-slate-700">Motivo do cancelamento <span className="text-red-600">*</span></label>
          <textarea rows={3} value={cancelMotivo} onChange={(e) => setCancelMotivo(e.target.value)}
            data-testid="handoff-cancel-motivo"
            placeholder="Ex: contacto duplicado, spam, cliente ja resolveu autonomamente..."
            className="w-full text-sm border border-tint rounded p-2 mt-1" />
          <div className="flex justify-end gap-2 pt-3 mt-2 border-t border-tint">
            <Btn variant="ghost" onClick={() => setShowCancel(false)}>Voltar</Btn>
            <Btn onClick={submitCancel} disabled={modalBusy} testid="handoff-cancel-submit" className="bg-red-600 hover:bg-red-700"><XCircle size={13} /> Cancelar handoff</Btn>
          </div>
        </WorkflowModal>
      )}

      {sel && isSuperAdmin && showDeleteConfirm && (
        <WorkflowModal title="Eliminar handoff" onClose={() => setShowDeleteConfirm(false)} testid="handoff-modal-delete">
          <div className="text-sm text-slate-700 mb-3">
            Tem a certeza que pretende <span className="font-bold text-red-700">eliminar permanentemente</span> este handoff?
          </div>
          <div className="bg-red-50 border border-red-200 rounded p-3 text-[11px] text-red-800 mb-4">
            <div className="font-semibold mb-1">Handoff a eliminar:</div>
            <div>{sel.nome || "Visitante"} - {sel.email || "-"}</div>
            <div>Tipo: {sel.tipo} | Estado: {sel.estado} | Criado: {sel.criado_em?.slice(0, 10)}</div>
            <div className="mt-2 text-red-700">Esta acao e irreversivel. O registo sera apagado da base de dados.</div>
          </div>
          <div className="flex justify-end gap-2 pt-2 border-t border-tint">
            <Btn variant="ghost" onClick={() => setShowDeleteConfirm(false)}>Cancelar</Btn>
            <Btn onClick={submitDelete} disabled={deleting} testid="handoff-delete-confirm" className="bg-red-700 hover:bg-red-800">
              <Trash2 size={13} /> {deleting ? "A eliminar..." : "Eliminar definitivamente"}
            </Btn>
          </div>
        </WorkflowModal>
      )}

      {sel && showCreateTask && (
        <WorkflowModal title="Criar tarefa a partir do handoff" onClose={() => setShowCreateTask(false)} testid="handoff-modal-task">
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="text-[11px] text-slate-600">Titulo <span className="text-red-600">*</span></label>
              <input value={taskForm.titulo} onChange={(e) => setTaskForm({ ...taskForm, titulo: e.target.value })}
                data-testid="handoff-task-titulo"
                placeholder="Ex: Agendar reuniao de proposta"
                className="w-full text-sm border border-tint rounded px-2 py-1.5 mt-1" />
            </div>
            <div>
              <label className="text-[11px] text-slate-600">Tipo</label>
              <select value={taskForm.tipo} onChange={(e) => setTaskForm({ ...taskForm, tipo: e.target.value })}
                data-testid="handoff-task-tipo"
                className="w-full text-sm border border-tint rounded px-2 py-1.5 mt-1 bg-white">
                <option value="follow-up">Follow-up</option>
                <option value="chamada">Chamada</option>
                <option value="reuniao">Reuniao</option>
                <option value="tarefa">Tarefa</option>
              </select>
            </div>
            <div>
              <label className="text-[11px] text-slate-600">Prioridade</label>
              <select value={taskForm.prioridade} onChange={(e) => setTaskForm({ ...taskForm, prioridade: e.target.value })}
                data-testid="handoff-task-prioridade"
                className="w-full text-sm border border-tint rounded px-2 py-1.5 mt-1 bg-white">
                <option value="alta">Alta</option>
                <option value="media">Media</option>
                <option value="baixa">Baixa</option>
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[11px] text-slate-600">Prazo</label>
              <input type="datetime-local" value={taskForm.prazo} onChange={(e) => setTaskForm({ ...taskForm, prazo: e.target.value })}
                data-testid="handoff-task-prazo"
                className="w-full text-sm border border-tint rounded px-2 py-1.5 mt-1" />
            </div>
            <div className="col-span-2">
              <label className="text-[11px] text-slate-600">Descricao</label>
              <textarea rows={3} value={taskForm.descricao} onChange={(e) => setTaskForm({ ...taskForm, descricao: e.target.value })}
                data-testid="handoff-task-descricao"
                className="w-full text-sm border border-tint rounded p-2 mt-1"
                placeholder="Contexto e checklist..." />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 mt-2 border-t border-tint">
            <Btn variant="ghost" onClick={() => setShowCreateTask(false)}>Cancelar</Btn>
            <Btn onClick={submitCreateTask} disabled={modalBusy} testid="handoff-task-submit"><Briefcase size={13} /> Criar tarefa</Btn>
          </div>
        </WorkflowModal>
      )}

      {sel && showEmail && (
        <WorkflowModal title="Responder ao cliente por email" onClose={() => setShowEmail(false)} testid="handoff-modal-email" wide>
          <div className="grid grid-cols-2 gap-2">
            <div className="col-span-2">
              <label className="text-[11px] text-slate-600">
                Template ({filteredTemplates.length} disponiveis para {sel.plataforma || "geral"})
              </label>
              <select value={emailForm.template_id} onChange={(e) => {
                const tid = e.target.value;
                const tpl = filteredTemplates.find((t) => t.id === tid);
                setEmailForm({ ...emailForm, template_id: tid,
                  assunto: tpl?.assunto || emailForm.assunto,
                  corpo_html: tpl?.corpo_html || emailForm.corpo_html });
              }}
                data-testid="handoff-email-template"
                className="w-full text-sm border border-tint rounded px-2 py-1.5 mt-1 bg-white">
                <option value="">-- corpo manual (sem template) --</option>
                {filteredTemplates.map((t) => (
                  <option key={t.id} value={t.id}>{t.nome}{t.plataforma ? ` [${t.plataforma}]` : ""} - {t.categoria}</option>
                ))}
              </select>
            </div>
            <div className="col-span-2">
              <label className="text-[11px] text-slate-600">Destinatario {sel.email ? <span className="text-slate-400">(pre-preenchido com o email do visitante)</span> : "(deixe vazio para usar o contacto associado)"}</label>
              <input value={emailForm.para} onChange={(e) => setEmailForm({ ...emailForm, para: e.target.value })}
                data-testid="handoff-email-para"
                placeholder="cliente@empresa.pt"
                className="w-full text-sm border border-tint rounded px-2 py-1.5 mt-1" />
            </div>
            <div className="col-span-2">
              <label className="text-[11px] text-slate-600">Assunto</label>
              <input value={emailForm.assunto} onChange={(e) => setEmailForm({ ...emailForm, assunto: e.target.value })}
                data-testid="handoff-email-assunto"
                className="w-full text-sm border border-tint rounded px-2 py-1.5 mt-1" />
            </div>
            <div className="col-span-2">
              <label className="text-[11px] text-slate-600">Corpo (HTML)</label>
              <textarea rows={8} value={emailForm.corpo_html} onChange={(e) => setEmailForm({ ...emailForm, corpo_html: e.target.value })}
                data-testid="handoff-email-corpo"
                className="w-full text-sm border border-tint rounded p-2 mt-1 font-mono text-[12px]"
                placeholder="<p>Ola {cliente_nome}, ...</p>" />
              <p className="text-[10px] text-slate-500 mt-1">Variaveis disponiveis: {"{cliente_nome}"}, {"{cliente_email}"}, {"{handoff_id}"}, {"{agente_nome}"}, {"{hoje}"}.</p>
            </div>

            {/* Assinatura do agente */}
            <div className="col-span-2">
              {agentSignature ? (
                <label className="flex items-center gap-2 cursor-pointer select-none text-[12px] text-slate-700">
                  <input type="checkbox" checked={inclSignature} onChange={(e) => setInclSignature(e.target.checked)}
                    data-testid="handoff-email-include-signature" className="rounded" />
                  Incluir assinatura do agente
                </label>
              ) : (
                <p className="text-[11px] text-amber-600">
                  Assinatura nao configurada. Defina-a em Definicoes - Assinatura.
                </p>
              )}
              {inclSignature && agentSignature && (
                <div className="mt-2 border border-slate-200 rounded bg-slate-50 p-3 text-[11px]"
                  data-testid="handoff-email-signature-preview">
                  <div className="text-[10px] uppercase text-slate-400 mb-1 font-semibold">Preview da assinatura</div>
                  <div dangerouslySetInnerHTML={{ __html: agentSignature }} />
                </div>
              )}
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-3 mt-2 border-t border-tint">
            <Btn variant="ghost" onClick={() => setShowEmail(false)}>Cancelar</Btn>
            <Btn onClick={submitEmail} disabled={modalBusy} testid="handoff-email-submit"><Mail size={13} /> Enviar email</Btn>
          </div>
        </WorkflowModal>
      )}

      {showSla && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40" {...overlayCloseHandlers(() => setShowSla(false))} data-testid="handoffs-sla-modal">
          <div className="bg-white rounded-lg border border-tint w-[min(680px,92vw)] max-h-[85vh] overflow-y-auto shadow-xl" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-4 py-3 border-b border-tint">
              <div className="text-sm font-bold text-navy">Configuracao SLA e Auto-atribuicao</div>
              <button onClick={() => setShowSla(false)} className="text-slate-400 hover:text-slate-700" data-testid="handoffs-sla-close">Fechar</button>
            </div>
            <div className="p-4 space-y-4">
              <div>
                <div className="text-[11px] font-semibold text-slate-700 mb-2">SLA por defeito (horas de primeiro contacto)</div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-[11px] text-slate-600">
                    Comercial (h)
                    <input type="number" step="0.25" min="0.1" value={slaCfg.default_comercial_h}
                      onChange={(e) => setSlaCfg({ ...slaCfg, default_comercial_h: Number(e.target.value) })}
                      className="mt-1 w-full text-sm border border-tint rounded px-2 py-1.5" data-testid="sla-default-comercial" />
                  </label>
                  <label className="text-[11px] text-slate-600">
                    Tecnico (h)
                    <input type="number" step="0.25" min="0.1" value={slaCfg.default_tecnico_h}
                      onChange={(e) => setSlaCfg({ ...slaCfg, default_tecnico_h: Number(e.target.value) })}
                      className="mt-1 w-full text-sm border border-tint rounded px-2 py-1.5" data-testid="sla-default-tecnico" />
                  </label>
                </div>
              </div>

              <div className="pt-1">
                <div className="flex items-center justify-between mb-2">
                  <div className="text-[11px] font-semibold text-slate-700">SLA por plataforma (opcional, sobrepõe o defeito)</div>
                </div>
                <div className="space-y-2">
                  {Object.keys(slaCfg.per_platform || {}).length === 0 && (
                    <div className="text-[11px] text-slate-400 italic">Nenhuma plataforma personalizada.</div>
                  )}
                  {Object.entries(slaCfg.per_platform || {}).map(([name, vals]) => (
                    <div key={name} className="grid grid-cols-4 gap-2 items-center">
                      <div className="text-xs font-semibold text-navy">{name}</div>
                      <input type="number" step="0.25" min="0.1" placeholder="Comercial (h)" value={vals?.comercial_h ?? ""}
                        onChange={(e) => setPerPlatform(name, "comercial_h", e.target.value)}
                        className="text-sm border border-tint rounded px-2 py-1.5" data-testid={`sla-plat-${name}-com`} />
                      <input type="number" step="0.25" min="0.1" placeholder="Tecnico (h)" value={vals?.tecnico_h ?? ""}
                        onChange={(e) => setPerPlatform(name, "tecnico_h", e.target.value)}
                        className="text-sm border border-tint rounded px-2 py-1.5" data-testid={`sla-plat-${name}-tec`} />
                      <button onClick={() => removePerPlatform(name)} className="text-[11px] text-red-600 hover:text-red-800" data-testid={`sla-plat-${name}-remove`}>Remover</button>
                    </div>
                  ))}
                  <div className="flex items-center gap-2 pt-1">
                    <input value={newPlatform} onChange={(e) => setNewPlatform(e.target.value)} placeholder="Plataforma (ex: Privaxia)"
                      className="text-sm border border-tint rounded px-2 py-1.5 flex-1" data-testid="sla-plat-new-name" />
                    <Btn variant="ghost" onClick={() => { if (newPlatform.trim()) { setPerPlatform(newPlatform.trim(), "comercial_h", slaCfg.default_comercial_h); setPerPlatform(newPlatform.trim(), "tecnico_h", slaCfg.default_tecnico_h); setNewPlatform(""); } }} testid="sla-plat-add">Adicionar</Btn>
                  </div>
                </div>
              </div>

              <div className="pt-1 border-t border-tint">
                <label className="inline-flex items-center gap-2 text-sm text-slate-700">
                  <input type="checkbox" checked={!!slaCfg.auto_assign} onChange={(e) => setSlaCfg({ ...slaCfg, auto_assign: e.target.checked })} data-testid="sla-auto-assign" />
                  Auto-atribuir novos pedidos por round-robin (baseado nas competencias/plataformas de cada membro)
                </label>
                <p className="text-[10px] text-slate-500 mt-1">
                  Para atribuir automaticamente, os membros da equipa devem ter a plataforma na lista de competencias
                  (Equipa &gt; membro &gt; competencias) OU pertencer a uma equipa (tipo=comercial ou suporte) que tenha a plataforma configurada.
                  Se nao houver candidato disponivel, o pedido fica em aberto para atribuicao manual pelos admins.
                </p>
              </div>

              <div className="pt-2 border-t border-tint">
                <div className="text-[11px] font-semibold text-slate-700 mb-2">Escalada por atraso (supervisor)</div>
                <div className="grid grid-cols-2 gap-3">
                  <label className="text-[11px] text-slate-600 col-span-2">
                    Email do supervisor
                    <input type="email" value={slaCfg.supervisor_email || ""}
                      onChange={(e) => setSlaCfg({ ...slaCfg, supervisor_email: e.target.value })}
                      placeholder="supervisor@cogniseg.com"
                      className="mt-1 w-full text-sm border border-tint rounded px-2 py-1.5" data-testid="sla-supervisor-email" />
                  </label>
                  <label className="text-[11px] text-slate-600 col-span-2">
                    Chat ID Telegram do supervisor (opcional)
                    <input value={slaCfg.supervisor_telegram_chat_id || ""}
                      onChange={(e) => setSlaCfg({ ...slaCfg, supervisor_telegram_chat_id: e.target.value })}
                      placeholder="ex: -1001234567890"
                      className="mt-1 w-full text-sm border border-tint rounded px-2 py-1.5" data-testid="sla-supervisor-tg" />
                  </label>
                </div>
                <p className="text-[10px] text-slate-500 mt-1">
                  Se preenchido, pedidos que ultrapassem o SLA sao encaminhados automaticamente ao supervisor (email + Telegram best-effort, verifica a cada 5 min).
                  O chat ID Telegram deve pertencer ao mesmo bot configurado em <b>Definicoes &gt; Canais &gt; Telegram</b>. Deixe em branco para usar o chat global de alertas.
                </p>
              </div>

              <div className="pt-2 border-t border-tint">
                <div className="text-[11px] font-semibold text-slate-700 mb-2">Notificacoes Telegram (equipa)</div>
                <div className="flex items-center justify-between gap-3 p-2.5 bg-slate-50 border border-tint rounded">
                  <div className="text-[11px] text-slate-600">
                    Estado: {tgStatus.configured && tgStatus.enabled
                      ? <span className="text-emerald-700 font-semibold">Activo{tgStatus.bot_username ? ` (@${tgStatus.bot_username})` : ""}</span>
                      : tgStatus.configured
                        ? <span className="text-amber-700 font-semibold">Configurado mas desligado</span>
                        : <span className="text-red-600 font-semibold">Nao configurado</span>}
                    <br/>
                    <span className="text-slate-500">
                      Config centralizada em <b>Definicoes &gt; Canais &gt; Telegram</b>.
                      As notificacoes de handoff sao enviadas automaticamente ao mesmo chat.
                    </span>
                  </div>
                  <Btn variant="ghost" onClick={testTelegram} disabled={tgTesting || !tgStatus.configured} testid="handoffs-tg-test">
                    {tgTesting ? "A enviar..." : "Enviar teste"}
                  </Btn>
                </div>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-tint">
                <Btn variant="ghost" onClick={() => setShowSla(false)}>Cancelar</Btn>
                <Btn onClick={saveSla} disabled={slaSaving} testid="sla-save">{slaSaving ? "A guardar..." : "Guardar"}</Btn>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
